import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  Check, 
  HelpCircle, 
  Users,
  DollarSign,
  Calendar,
  Zap
} from 'lucide-react';
import { CURRENCY_OPTIONS } from '../constants';
import { cn } from '@/lib/utils';

interface BasePricingSelectorProps {
  selectedType: 'fixed' | 'per_person' | 'per_day' | 'per_person_day';
  onTypeChange: (type: 'fixed' | 'per_person' | 'per_day' | 'per_person_day') => void;
  basePrice: number;
  currency: string;
  showExamples?: boolean;
}

// All pricing options - unified and simplified
const ALL_PRICING_OPTIONS = [
  {
    value: 'per_person' as const,
    label: 'Per Person',
    icon: Users,
    description: 'Price scales with group size',
    example: '$50 × 4 people = $200'
  },
  {
    value: 'fixed' as const,
    label: 'Fixed Amount',
    icon: DollarSign,
    description: 'Same price regardless of group size',
    example: '$200 total (always same)'
  },
  {
    value: 'per_day' as const,
    label: 'Per Day',
    icon: Calendar,
    description: 'Price scales with duration',
    example: '$75 × 3 days = $225'
  },
  {
    value: 'per_person_day' as const,
    label: 'Per Person Per Day',
    icon: Zap,
    description: 'Price scales with both people and days',
    example: '$30 × 4 people × 3 days = $360'
  }
];

export function BasePricingSelector({ 
  selectedType, 
  onTypeChange, 
  basePrice,
  currency,
  showExamples = true 
}: BasePricingSelectorProps) {
  const currencySymbol = CURRENCY_OPTIONS.find(c => c.value === currency)?.symbol || '$';
  
  const getExamplePrice = (type: typeof selectedType) => {
    const price = basePrice || 50; // Use actual price or example
    
    switch (type) {
      case 'fixed':
        return `${currencySymbol}${price} total`;
      case 'per_person':
        return `${currencySymbol}${price} × 4 people = ${currencySymbol}${price * 4}`;
      case 'per_day':
        return `${currencySymbol}${price} × 3 days = ${currencySymbol}${price * 3}`;
      case 'per_person_day':
        return `${currencySymbol}${price} × 4 people × 3 days = ${currencySymbol}${price * 4 * 3}`;
      default:
        return `${currencySymbol}${price}`;
    }
  };

  const PricingOption = ({ option }: { 
    option: typeof ALL_PRICING_OPTIONS[0]
  }) => {
    const isSelected = selectedType === option.value;
    const Icon = option.icon;

    return (
      <div
        className={cn(
          "relative cursor-pointer rounded-lg border-2 p-3 transition-all duration-200",
          isSelected
            ? "border-blue-500 bg-blue-50 shadow-sm"
            : "border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm"
        )}
        onClick={() => onTypeChange(option.value)}
      >
        {/* Selection indicator */}
        {isSelected && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
            <Check className="w-3 h-3 text-white" />
          </div>
        )}

        <div className="flex items-start gap-3 mt-1">
          <div className={cn(
            "p-2 rounded-lg",
            isSelected ? "bg-blue-500" : "bg-slate-100"
          )}>
            <Icon className={cn(
              "w-4 h-4",
              isSelected ? "text-white" : "text-slate-600"
            )} />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className={cn(
                "font-medium text-sm",
                isSelected ? "text-blue-700" : "text-slate-900"
              )}>
                {option.label}
              </h4>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="w-3 h-3 text-slate-400" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p className="text-xs">{option.description}</p>
                    {basePrice > 0 && (
                      <p className="text-xs text-blue-600 mt-1 font-medium">
                        Example: {getExamplePrice(option.value)}
                      </p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">{option.description}</p>
            
            {/* Show example only for selected option */}
            {isSelected && basePrice > 0 && (
              <div className="mt-2 text-xs text-blue-600 font-medium">
                Example: {getExamplePrice(option.value)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-medium text-slate-700">Pricing Model</h3>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <HelpCircle className="w-4 h-4 text-slate-400" />
            </TooltipTrigger>
            <TooltipContent className="max-w-md">
              <p className="text-sm">Choose how your base price scales with group size or duration.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* All Pricing Options */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {ALL_PRICING_OPTIONS.map((option) => (
          <PricingOption key={option.value} option={option} />
        ))}
      </div>
    </div>
  );
} 